import { forwardRef, createElement } from 'react';

const nextShimsCache = new Map<string, Record<string, unknown>>();

const resolveImageSrc = (src: unknown): string => {
  if (
    src &&
    typeof src === 'object' &&
    'src' in (src as Record<string, unknown>)
  ) {
    return String((src as Record<string, unknown>).src);
  }
  return typeof src === 'string' ? src : '';
};

const buildQueryString = (query: unknown): string => {
  if (!query || typeof query !== 'object') return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const buildHashSuffix = (hash: unknown): string => {
  if (typeof hash !== 'string') return '';
  return hash.startsWith('#') ? hash : `#${hash}`;
};

const resolveHref = (href: unknown): string => {
  if (typeof href === 'string') return href;
  if (href instanceof URL) return href.toString();
  if (!href || typeof href !== 'object') return '';

  const obj = href as Record<string, unknown>;
  const pathname = typeof obj.pathname === 'string' ? obj.pathname : '/';
  return pathname + buildQueryString(obj.query) + buildHashSuffix(obj.hash);
};

const createNextShims = (remoteOrigin: string) => {
  const cached = nextShimsCache.get(remoteOrigin);
  if (cached) return cached;

  const nullRouterModule = {
    __esModule: true,
    useRouter: () => null,
    default: { useRouter: () => null },
  };

  const linkShim = forwardRef(
    (props: Record<string, unknown>, ref: unknown) => {
      const { href, replace: _r, prefetch: _p, ...rest } = props;
      return createElement('a', {
        ...rest,
        href: resolveHref(href),
        ref,
      });
    },
  );
  linkShim.displayName = 'NextLinkShim';

  const imageShim = forwardRef(
    (props: Record<string, unknown>, ref: unknown) => {
      const {
        src,
        fill: _f,
        loader: _l,
        quality: _q,
        priority: _pr,
        placeholder: _ph,
        blurDataURL: _b,
        unoptimized: _u,
        ...rest
      } = props;

      let imgSrc = resolveImageSrc(src);
      if (imgSrc.startsWith('/_next/')) {
        imgSrc = `${remoteOrigin}${imgSrc}`;
      }

      return createElement('img', { ...rest, src: imgSrc, ref });
    },
  );
  imageShim.displayName = 'NextImageShim';

  const shims = {
    'next/compat/router': nullRouterModule,
    'next/router': nullRouterModule,
    'next/link': { __esModule: true, default: linkShim },
    'next/image': { __esModule: true, default: imageShim },
  };
  nextShimsCache.set(remoteOrigin, shims);
  return shims;
};

export const registerNavigationShim = async (
  container: { get: (m: string) => Promise<() => Record<string, unknown>> },
  shim: { setSharedModules: (s: Record<string, unknown>) => void },
  remoteOrigin: string,
) => {
  const nextShims = createNextShims(remoteOrigin);
  let sharedModules: Record<string, unknown> = nextShims;
  try {
    const navFactory = await container.get('./navigationShim');
    const navModule = navFactory();

    sharedModules = {
      'next/navigation': navModule,
      ...nextShims,
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[MF] Remote "${remoteOrigin}" does not expose ./navigationShim; navigation shims will not be registered`,
        error,
      );
    }
  } finally {
    shim.setSharedModules(sharedModules);
  }
};
